import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-overview-pagination',
  standalone: true,
  imports: [MatIcon, TranslatePipe],
  templateUrl: './overview-pagination.component.html',
  styleUrl: './overview-pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverviewPaginationComponent {
  readonly pageIndex = input.required<number>();
  readonly pageSize = input.required<number>();
  readonly totalCount = input.required<number>();
  readonly pageSizeOptions = input<readonly number[]>([5, 10, 25]);

  readonly pageIndexChange = output<number>();
  readonly pageSizeChange = output<number>();

  protected readonly clampedPageIndex = computed(() => {
    const total = this.totalCount();
    const size = this.pageSize();
    if (total === 0 || size === 0) return 0;
    return Math.min(this.pageIndex(), Math.ceil(total / size) - 1);
  });

  protected readonly pageStart = computed(() =>
    this.totalCount() === 0 ? 0 : this.clampedPageIndex() * this.pageSize() + 1,
  );

  protected readonly pageEnd = computed(() =>
    Math.min((this.clampedPageIndex() + 1) * this.pageSize(), this.totalCount()),
  );

  protected readonly atFirstPage = computed(() => this.clampedPageIndex() === 0);
  protected readonly atLastPage = computed(() => this.pageEnd() >= this.totalCount());

  protected prev(): void {
    this.pageIndexChange.emit(Math.max(0, this.clampedPageIndex() - 1));
  }

  protected next(): void {
    const max = Math.ceil(this.totalCount() / this.pageSize()) - 1;
    this.pageIndexChange.emit(Math.min(max, this.clampedPageIndex() + 1));
  }

  protected onSizeChange(size: number): void {
    this.pageSizeChange.emit(size);
  }
}
